/**
 * A practice's dedicated proxy port — the Oxylabs Dedicated ISP gateway port
 * that maps to the fixed address EVERY portal login of that practice leaves
 * from, Revolution as well as VSP and EyeMed
 * (`organizations.integrations.oxylabs.proxyPort`). One port is one static
 * IP, so assigning a port per practice is what keeps two practices off one
 * address: a practice a portal sours on never takes another down with it,
 * and practices can run side by side because each one is its own customer
 * on the wire. Assigned by VitalRev staff on the console's Scraper Proxy
 * section.
 *
 * The port is the CONDITION for a practice to scrape at all: nothing of a
 * practice's runs without one. Judged twice — at the producer as part of
 * "configured" (`laneGate`: the clinic's vendor record first, then the
 * practice's port, so a missing port is reported only for a clinic whose
 * record is configured), and in the worker, which on the job platform refuses
 * a unit that reaches it without a port.
 *
 * Shared frontend/backend so the staff console, the callable, the dispatcher
 * and the scrape worker agree on what a valid port is and what a bad stored
 * value means (nothing — never a half-trusted address).
 */
import {integrationConfigured} from "./credentialSecrets.js";

/** Doc key: `organizations/{orgId}.integrations.{PROXY_INTEGRATION_KEY}.proxyPort`. */
export const PROXY_INTEGRATION_KEY = "oxylabs";

export const PROXY_PORT_MIN = 1;
export const PROXY_PORT_MAX = 65535;

/** A whole TCP port number. */
export function isValidProxyPort(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) &&
    value >= PROXY_PORT_MIN && value <= PROXY_PORT_MAX;
}

/** The stored value when it is a valid port, null otherwise — absent, cleared
 *  or malformed all read as "no dedicated address", never as a guess. */
export function resolveProxyPort(stored: unknown): number | null {
  return isValidProxyPort(stored) ? stored : null;
}

/** The port the org doc carries under `integrations.oxylabs.proxyPort`, read
 *  off a raw document; null when unassigned or malformed. */
export function proxyPortOf(orgData: unknown): number | null {
  const integrations = (orgData as {integrations?: unknown} | undefined)?.integrations;
  const entry = (integrations as Record<string, unknown> | undefined)?.[PROXY_INTEGRATION_KEY];
  return resolveProxyPort((entry as {proxyPort?: unknown} | undefined)?.proxyPort);
}

/** The sentence every producer uses for a configured clinic of a port-less
 *  practice: the manual pull and the staff rerun refuse with it, a vision row
 *  carries it as its data issue. */
export const NO_PROXY_PORT_MESSAGE =
  "No dedicated address is assigned to this practice — contact VitalRev support";

export type LaneGateVerdict =
  | {kind: "ok"}
  /** The clinic's record for the vendor is not configured (no secret-store
   *  credential) — reported exactly as before, whether or not the practice
   *  holds a port. */
  | {kind: "not_configured"}
  /** The record is configured but the practice holds no port. */
  | {kind: "no_proxy_port"};

/**
 * Is a clinic configured for a vendor's lane? The record is judged first
 * (`integrationConfigured`), the practice's port second — one decision with
 * two different sentences, so a missing port is never reported for a clinic
 * whose credential is what is missing.
 */
export function laneGate(
  record: {secretName?: unknown} | null | undefined,
  proxyPort: number | null,
): LaneGateVerdict {
  if (!integrationConfigured(record)) return {kind: "not_configured"};
  if (resolveProxyPort(proxyPort) === null) return {kind: "no_proxy_port"};
  return {kind: "ok"};
}
